import React, { useCallback, useMemo, useState } from 'react'
import { Menu, MenuItem, Portal, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Grid } from '@mui/material'
import { navigate, useParams } from '@reach/router'
import Theme from '@refera/ui-core'
import {
  ArrowRight3,
  DollarCircle,
  MessageQuestion,
  Danger,
  Star,
  Edit2,
  Trash,
} from '@refera/ui-icons'
import { useConfirm, Typography } from '@refera/ui-web'
import classnames from 'classnames'
import format from 'date-fns/format'
import ptBR from 'date-fns/locale/pt-BR'
import { FaStar } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import DropdownIcon from '_assets/icons/ic-dropdown.svg'
import Button from '_components/button'
import NewCommentModal from '_components/modal/new-structure-comment-modal'
import SaasNewCommentModal from '_components/modal/saas-new-comment-modal'
import IconButton from '_components/svg/icon-button'
import useToggle from '_hooks/use-toggle'
import useRolePermission from '_hooks/use-role-permission'
import {
  getTramitesPath,
  getApiErrorMessage,
  isHelpRequestReadOnlyForIntermediary,
  INTERMEDIARY_READ_ONLY_EDIT_TOOLTIP,
  INTERMEDIARY_READ_ONLY_DELETE_TOOLTIP,
} from '_hooks/use-help-request-read-only'
import {
  deleteMessage,
  getAllNotes,
  updateNote,
  deletePaymentAnticipation,
} from '_modules/messages/actions'
import PaymentAdvanceApproval from '_views/finance/payment-advance'
import LogDate from '../log-date'
import useStyles from './styles'
import Svg from '_/components/svg'
import InfoIcon from '_assets/icons/ic-info-circle.svg'
import TickIcon from '_assets/icons/ic-tick-circle.svg'
import DollarCircleIcon from '_assets/icons/ic-dollar-circle.svg'
import CloseCircleIcon from '_assets/icons/ic-close-circle.svg'
import { userSelector } from '_modules/authentication/selectors'
import JustifiedDelayForm from '_views/service-order/justified-delay/justified-delay-form/index'
import DialogModal, { WARNING_MODAL } from '_components/modal/modal-dialog'
import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'
import { formatCurrency, formatDate } from '_/utils/helpers'
import PropTypes from 'prop-types'
import { useToast } from '_/hooks/use-toast'

// TODO: Decrease component complexity by using it as base to other ones
// e.g. Other components: JustifiedDelayAnnotation, HelpRequestAnnotation, and so on
//      There would be using GeneralAnnotation passing the content as children,
//      and dynamic content (as icon, icon's classNames, handleDelete and handleEditChoice) as prop
const GeneralAnnotation = ({ data, noteType, tradesmanList, isFirstItem = false }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { serviceOrderId } = useParams()
  const user = useSelector(userSelector)
  const { isSAAS, isIntermediary, isAdmin } = useRolePermission()

  const isAnnotationReadOnly = useMemo(
    () =>
      isHelpRequestReadOnlyForIntermediary(data, {
        isIntermediary,
        isSAAS,
        isAdmin,
        noteType,
      }),
    [noteType, data, isIntermediary, isSAAS, isAdmin]
  )

  const [anchorEl, setAnchorEl] = useState(null)
  const [paymentAdvanceModalOpen, togglePaymentAdvanceModalOpen] = useToggle()
  const [paymentAdvanceItem, setPaymentAdvanceItem] = useState(null)
  const [successSnackbar, toggleSuccessSnackbar] = useToggle()
  const [openJustifiedDelayForm, toggleJustifiedDelayForm] = useToggle()
  const [helpObj, setHelpObj] = useState(null)
  const [newDataAtt, setNewDataAtt] = useState(null)
  const [newCommentModal, setNewCommentModal] = useState({ isOpen: false, title: '' })
  const [toggleHelpView] = useToggle()
  const simpleDateFormat = 'dd/MM/yyy'

  const name = data ? data?.senderName?.split(' ') : ''
  const initials =
    name && name[0][0] + name[name.length - 1][0]
      ? (name[0][0] + name[name.length - 1][0])?.toUpperCase()
      : ''

  const [modalDialog, setModalDialog] = useState({ isOpen: false, subTitle: '' })
  const [permissionModal, setPermissionModal] = useState({ isOpen: false, subTitle: '' })
  const [dialogMessage, setDialogMessage] = useState('')

  const { isConfirmed } = useConfirm()

  const openDropdown = Boolean(anchorEl)
  const { showToast } = useToast()

  const handleUpdate = useCallback(() => {
    showToast({
      type: 'success',
      message: 'Ação realizada com sucesso.',
    })
    dispatch(getAllNotes({ serviceOrderId }))
  }, [dispatch, getAllNotes, serviceOrderId])

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleToggleIsImportant = useCallback(() => {
    const nextIsImportant = !data?.isImportant

    dispatch(
      updateNote(data.id, {
        type: data.noteType,
        isImportant: nextIsImportant,
      })
    ).catch(error =>
      showToast({
        type: 'error',
        message: getApiErrorMessage(error),
      })
    )
  }, [data, dispatch, showToast])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onCommentModal = useCallback(() => {
    setNewCommentModal({
      isOpen: true,
      title: 'Comentários',
      comment: data,
    })
  }, [data])

  const onDeleteMessage = useCallback(
    async removeMessage => {
      setDialogMessage(removeMessage)
      const confirmed = await isConfirmed()
      if (confirmed) {
        if (data.noteType === 'payment_anticipation') {
          dispatch(deletePaymentAnticipation(data.id)).then(() =>
            dispatch(getAllNotes({ serviceOrderId }))
          )
        } else {
          dispatch(deleteMessage(data.id)).then(() => dispatch(getAllNotes({ serviceOrderId })))
        }
      }
    },
    [dialogMessage, data]
  )

  const handleDelete = useCallback(() => {
    switch (data.noteType) {
      case 'comment':
        onDeleteMessage('Você tem certeza que deseja excluir este registro?')
        break
      case 'help_request':
        handleModalsChoice('redirect')
        break
      case 'justified_delay':
        handleModalsChoice('redirect')
        break
      default:
        break
    }
  }, [data])

  const handleModalsChoice = type => {
    if (type === 'permission') {
      setPermissionModal({
        isOpen: true,
        subTitle: 'Você não tem permissão para esta ação.',
        type: WARNING_MODAL,
      })
    } else if (type === 'redirect') {
      setModalDialog({
        isOpen: true,
        subTitle: 'Para realizar essa ação você será direcionado para a página específica.',
        type: WARNING_MODAL,
        isClose: handleGetHelpRequest,
      })
    }
  }

  const handleEditChoice = useCallback(() => {
    switch (noteType) {
      case 'COMMENT':
        onCommentModal()
        break
      case 'HELP_REQUEST':
        handleModalsChoice('redirect')
        break
      case 'JUSTIFIED_DELAY':
        if (user.getRoles.includes('cs_refera')) {
          handleModalsChoice('redirect')
        } else {
          handleModalsChoice('permission')
        }
        break
      case 'PAYMENT_ANTICIPATION':
        user.getRoles.includes('cs_refera')
          ? handleGetAdvancedOrder()
          : handleModalsChoice('permission')
        break
      default:
        return null
    }
    return null
  }, [noteType, data])

  const handleGetAdvancedOrder = useCallback(() => {
    setPaymentAdvanceItem(data)

    togglePaymentAdvanceModalOpen()
  }, [data, togglePaymentAdvanceModalOpen])

  const renderMenuItems = useMemo(() => {
    const editableNoteTypes = ['COMMENT', 'HELP_REQUEST', 'JUSTIFIED_DELAY', 'PAYMENT_ANTICIPATION']

    const items = [
      {
        id: 'edit',
        show: editableNoteTypes.includes(noteType),
        disabled: isAnnotationReadOnly,
        icon: (
          <span className={styles.menuItemIcon}>
            <Edit2
              className={classnames(styles.editIcon, {
                [styles.readOnlyActionIcon]: isAnnotationReadOnly,
              })}
            />
          </span>
        ),
        label: (
          <Typography
            className={classnames(styles.menuItemLabel, styles.edit, {
              [styles.readOnlyActionLabel]: isAnnotationReadOnly,
            })}
          >
            Editar
          </Typography>
        ),
        onClick: handleEditChoice,
        readOnlyTooltip: INTERMEDIARY_READ_ONLY_EDIT_TOOLTIP,
      },
      {
        id: 'delete',
        show: !noteType.includes('PAYMENT_ANTICIPATION'),
        disabled: isAnnotationReadOnly,
        icon: (
          <span className={styles.menuItemIcon}>
            <Trash
              className={classnames(styles.trashIcon, {
                [styles.readOnlyActionIcon]: isAnnotationReadOnly,
              })}
            />
          </span>
        ),
        label: (
          <Typography
            className={classnames(styles.menuItemLabel, styles.delete, {
              [styles.readOnlyActionLabel]: isAnnotationReadOnly,
            })}
          >
            Excluir
          </Typography>
        ),
        onClick: handleDelete,
        readOnlyTooltip: INTERMEDIARY_READ_ONLY_DELETE_TOOLTIP,
      },
    ]

    return items
      .filter(item => item.show)
      .map(item => {
        const menuItem = (
          <MenuItem
            key={item.id}
            className={styles.menuItem}
            disabled={item.disabled}
            onClick={item.disabled ? undefined : item.onClick}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        )

        if (!item.disabled) {
          return menuItem
        }

        return (
          <Tooltip key={item.id} title={item.readOnlyTooltip} placement="left">
            <span className={styles.disabledMenuItemWrapper}>{menuItem}</span>
          </Tooltip>
        )
      })
  }, [handleDelete, handleEditChoice, isAnnotationReadOnly, noteType, styles])

  const tramitesPath = useMemo(() => getTramitesPath(data.id), [data.id])

  const handleGetHelpRequest = useCallback(() => {
    navigate(tramitesPath)
  }, [tramitesPath])

  const handleClearMessage = () => {
    if (helpObj?.answer?.length > 0 && helpObj?.resolved) {
      toggleHelpView()
    } else {
      toggleJustifiedDelayForm()
    }
    setHelpObj(undefined)
  }

  const handleSuccessSnackbar = useCallback(() => {
    toggleSuccessSnackbar()
  }, [toggleSuccessSnackbar])

  const handleCloseModal = useCallback(() => {
    togglePaymentAdvanceModalOpen()
  }, [togglePaymentAdvanceModalOpen])

  const renderFormatedDate = (date, dateFormat) => {
    const result = format(new Date(date), dateFormat, {
      locale: ptBR,
    })

    return result
  }

  const renderPaymentStatus = useMemo(() => {
    if (data.status === 'pending') {
      return (
        <Grid className={styles.statusTag}>
          <Svg icon={InfoIcon} className={styles.helpRequestIcon} />
          <Typography>Pendente</Typography>
        </Grid>
      )
    }
    if (data.status === 'approved' && data.activeBatchId) {
      return (
        <Grid className={styles.statusTag}>
          <Svg icon={TickIcon} className={styles.helpRequestIcon} />
          <Typography>Em pagamento</Typography>
        </Grid>
      )
    }
    if (data.status === 'approved' || data.status === 'paid') {
      const isPaid = data.datetimePayed || data.status === 'paid'
      return (
        <Grid className={styles.statusTag}>
          <Svg icon={isPaid ? DollarCircleIcon : TickIcon} className={styles.helpRequestIcon} />
          <Typography>{isPaid ? 'Pago' : 'Aprovado'}</Typography>
        </Grid>
      )
    }
    if (data.status === 'reproved') {
      return (
        <Grid className={styles.statusTag}>
          <Svg icon={CloseCircleIcon} className={styles.helpRequestIcon} />
          <Typography>Reprovado</Typography>
        </Grid>
      )
    }

    return null
  })

  return (
    <Grid
      className={classnames(styles.annotationItem, {
        [styles.firstAnnotationItem]: isFirstItem,
      })}
    >
      <LogDate data={data?.createdAt} />
      <Grid className={styles.content}>
        {noteType === 'JUSTIFIED_DELAY' && (
          <Grid className={classnames(styles.icon, styles.justifiedDelay)}>
            <IconButton disabled className={styles.commentIcon}>
              <Danger color="black" />
            </IconButton>
          </Grid>
        )}

        {noteType === 'HELP_REQUEST' && (
          <Grid className={classnames(styles.icon, styles.helpRequest)}>
            <IconButton disabled className={styles.commentIcon}>
              <MessageQuestion color="white" />
            </IconButton>
          </Grid>
        )}

        {noteType === 'PAYMENT_ANTICIPATION' && (
          <Grid className={classnames(styles.icon, styles.paymentAnticipation)}>
            <IconButton disabled className={styles.commentIcon}>
              <DollarCircle color="black" />
            </IconButton>
          </Grid>
        )}

        {noteType === 'COMMENT' && (
          <Grid className={styles.icon}>
            <Typography className={styles.title}>{initials}</Typography>
          </Grid>
        )}

        <Grid className={styles.wrapper}>
          <Grid className={styles.raw}>
            <Typography className={styles.title}>{data.senderName}</Typography>
            <Grid className={styles.buttons}>
              {data.isImportant ? (
                <FaStar
                  size={18}
                  className={styles.styleEnabled}
                  onClick={handleToggleIsImportant}
                />
              ) : (
                <Star
                  size={18}
                  className={styles.styleDisabled}
                  onClick={handleToggleIsImportant}
                />
              )}
              <IconButton
                icon={DropdownIcon}
                aria-controls={openDropdown ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openDropdown ? 'true' : undefined}
                onClick={handleClick}
              />
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={openDropdown}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                  style: {
                    marginTop: 40,
                    marginLeft: 8,
                  },
                }}
              >
                {renderMenuItems}
              </Menu>
            </Grid>
          </Grid>
          {noteType === 'JUSTIFIED_DELAY' && (
            <Grid className={styles.bodyContent}>
              <Typography className={styles.description}>
                <span className={styles.span}>Solicitante:</span>
                <span className={styles.spanMesage}>{data.requester}</span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.span}>Tipo de Justificativa:</span>
                <span className={styles.spanMesage}> {data.justifieType}</span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.span}>Motivo: </span>
                <span className={styles.spanMesage}>{data.reason}</span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.span}>Resolverei até: </span>
                <span className={styles.spanMesage}> {formatDate(data?.expectedDeadline)}</span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.spanMesage}>{data.message}</span>
              </Typography>
              <Button
                variant="ghost"
                className={styles.link}
                to={tramitesPath}
                endIcon={<ArrowRight3 color={Theme.Colors.Primary.Base} />}
              >
                Ver mais detalhes
              </Button>
              <Grid className={styles.tagsRow}>
                <span className={styles.tag}>Atraso Justificado</span>
                {isAnnotationReadOnly && <span className={styles.tag}>Somente leitura</span>}
              </Grid>
              {openJustifiedDelayForm && (
                <JustifiedDelayForm
                  closeModal={handleClearMessage}
                  message={newDataAtt ?? helpObj}
                  setMessage={setNewDataAtt}
                  tradesmanInfo={tradesmanList}
                />
              )}
            </Grid>
          )}
          {noteType === 'HELP_REQUEST' && (
            <Grid className={styles.bodyContent}>
              {data.resolved === false && (
                <Grid className={styles.statusTag}>
                  <Svg icon={InfoIcon} className={styles.helpRequestIcon} />
                  <Typography>Pendente</Typography>
                </Grid>
              )}
              {data.resolved === true && (
                <Grid className={styles.statusTag}>
                  <Svg icon={TickIcon} className={styles.helpRequestIcon} />
                  <Typography>Resolvido</Typography>
                </Grid>
              )}
              <Typography className={styles.description}>
                <span className={styles.span}>Assunto: </span>
                <span className={styles.spanMesage}>{data.subject}</span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.spanMesage}>{data.message}</span>
              </Typography>
              <Button
                variant="ghost"
                className={styles.link}
                to={tramitesPath}
                endIcon={<ArrowRight3 color={Theme.Colors.Primary.Base} />}
              >
                Ver mais detalhes
              </Button>
              <Grid className={styles.tagsRow}>
                <span className={styles.tag}>Item de ajuda</span>
                {isAnnotationReadOnly && <span className={styles.tag}>Somente leitura</span>}
              </Grid>
            </Grid>
          )}
          {noteType === 'PAYMENT_ANTICIPATION' && (
            <Grid className={styles.bodyContent}>
              {renderPaymentStatus}
              <Typography className={styles.description}>
                <span className={styles.span}>Data do pedido de antecipação: </span>
                <span className={styles.spanMesage}>
                  {renderFormatedDate(data.createdAt, simpleDateFormat)}
                </span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.span}>Solicitante: </span>
                <span className={styles.spanMesage}> {data.senderName} </span>
              </Typography>
              <Typography className={styles.description}>
                <span className={styles.span}>Valor: </span>
                <span className={styles.spanMesage}>{formatCurrency(data.value)} </span>
              </Typography>
              <Button
                variant="ghost"
                className={styles.link}
                endIcon={<ArrowRight3 color={Theme.Colors.Primary.Base} />}
                onClick={handleGetAdvancedOrder}
              >
                Ver mais detalhes
              </Button>
              <Grid className={styles.tagsRow}>
                <span className={styles.tag}>Antecipação</span>
              </Grid>
              {paymentAdvanceModalOpen && (
                <PaymentAdvanceApproval
                  paymentAdvanceItem={paymentAdvanceItem}
                  setSuccessSnackbar={handleSuccessSnackbar}
                  closeModal={handleCloseModal}
                />
              )}
              <Portal>
                <Snackbar
                  open={successSnackbar}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  autoHideDuration={6000}
                  onClose={toggleSuccessSnackbar}
                >
                  <Alert
                    onClose={toggleSuccessSnackbar}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                  >
                    <Typography>Sua ação foi realizada com sucesso</Typography>
                  </Alert>
                </Snackbar>
              </Portal>
            </Grid>
          )}
          {noteType === 'COMMENT' && (
            <>
              <Grid className={styles.bodyContent}>
                <Typography className={styles.description}>
                  <span className={styles.spanMesage}>{data.message}</span>
                </Typography>
                <Button
                  variant="ghost"
                  className={styles.link}
                  onClick={onCommentModal}
                  endIcon={<ArrowRight3 color={Theme.Colors.Primary.Base} />}
                >
                  Ver mais detalhes
                </Button>
                <Grid className={styles.tagsRow}>
                  <span className={styles.tag}>{data.visibleTo}</span>
                </Grid>
              </Grid>
              {isSAAS ? (
                <SaasNewCommentModal
                  newCommentModal={newCommentModal}
                  setNewCommentModal={setNewCommentModal}
                  handleUpdate={handleUpdate}
                />
              ) : (
                <NewCommentModal
                  newCommentModal={newCommentModal}
                  setNewCommentModal={setNewCommentModal}
                  handleUpdate={handleUpdate}
                />
              )}
            </>
          )}
          {modalDialog && <DialogModal modalDialog={modalDialog} setModalDialog={setModalDialog} />}
          {permissionModal && (
            <DialogModal modalDialog={permissionModal} setModalDialog={setPermissionModal} />
          )}
          {dialogMessage && <ConfirmRemoveDialog message={dialogMessage} />}
        </Grid>
      </Grid>
    </Grid>
  )
}

GeneralAnnotation.propTypes = {
  data: PropTypes.shape({
    expectedDeadline: PropTypes.instanceOf(Date),
  }),
}

GeneralAnnotation.defaultProps = {
  data: {
    expectedDeadline: new Date(),
  },
}

export default React.memo(GeneralAnnotation)
